import { useApolloClient } from '@apollo/client';
import { useApi } from '../../integrations/lavva/hooks/api';
import { useCacheStore } from '../../integrations/lavva/store/cache';

const queryData = [
  { queryName: 'InstallationGetAll', query: 'userInstallations' },
  { queryName: 'DashboardItems', query: 'userGroups' },
  { queryName: 'AllActions', query: 'userActions' },
  { queryName: 'AllTriggers', query: 'allTriggers' },
];

export const useRefetchData = () => {
  const apolloClient = useApolloClient();
  const { setGroupsSkip, setActionsSkip, setInstallationsSkip } = useCacheStore((s) => s);
  const { refetchChannels, refetchDevices } = useApi();

  const getActiveQueries = () => {
    const mapOfActiveQueries = apolloClient.getObservableQueries();
    const activeQueries = Array.from(mapOfActiveQueries)
      .map((subArray) => subArray[1].queryName || '')
      .filter((x) => x);

    return activeQueries;
  };

  const checkIfCanFetch = (queryName: string) => {
    const activeQueries = getActiveQueries();
    return activeQueries.find((x) => x === queryName);
  };

  const clearCache = (fieldName: string) => {
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName,
    });
  };

  const refetchActions = async () => {
    setActionsSkip(false);

    if (checkIfCanFetch('AllActions')) {
      clearCache('userActions');
      apolloClient.cache.gc();

      return await apolloClient.refetchQueries({ include: ['AllActions'] });
    }
  };

  const refetchDashboard = async () => {
    setGroupsSkip(false);

    if (checkIfCanFetch('DashboardItems')) {
      clearCache('userGroups');
      apolloClient.cache.gc();

      return await apolloClient.refetchQueries({ include: ['DashboardItems'] });
    }
  };

  const refetchTriggers = async () => {
    if (checkIfCanFetch('AllTriggers')) {
      return await apolloClient.refetchQueries({ include: ['AllTriggers'] });
    }
  };

  const refetchChannelDetails = async () => {
    if (checkIfCanFetch('ChannelsDetails')) {
      clearCache('userChannel');
      apolloClient.cache.gc();
      return await apolloClient.refetchQueries({ include: ['ChannelsDetails'] });
    }
  };

  const refetchAll = async () => {
    setGroupsSkip(false);
    setActionsSkip(false);
    setInstallationsSkip(false);

    refetchDevices();
    refetchChannels();

    const activeQueries = getActiveQueries();
    const queries = queryData.filter((x) => activeQueries.includes(x.queryName));

    queries.map((x) => clearCache(x.query));
    apolloClient.cache.gc();

    return await apolloClient.refetchQueries({
      include: queries.map((x) => x.queryName),
    });
  };

  return {
    refetchDevices,
    refetchChannels,
    refetchActions,
    refetchDashboard,
    refetchTriggers,
    refetchChannelDetails,
    refetchAll,
  };
};
